import { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react'
import { SiX, SiLinkedin, SiWechat } from 'react-icons/si'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { IMAGES } from '@/assets/images'
import { useLanguage } from '@/lib/index'
import { fadeInUp, staggerContainer, staggerItem } from '@/lib/motion'

type ContactFormData = {
  name: string
  company: string
  email: string
  phone: string
  serviceType: string
  message: string
}

export default function Contact() {
  const { t } = useLanguage()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const formSchema = z.object({
    name: z.string().min(2, { message: t.contactPage.form.validation.nameMin }),
    company: z.string().min(2, { message: t.contactPage.form.validation.companyMin }),
    email: z.string().email({ message: t.contactPage.form.validation.emailInvalid }),
    phone: z.string().min(8, { message: t.contactPage.form.validation.phoneMin }),
    serviceType: z.string().min(1, { message: t.contactPage.form.validation.serviceTypeRequired }),
    message: z.string().min(10, { message: t.contactPage.form.validation.messageMin }),
  })

  const form = useForm<ContactFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      company: '',
      email: '',
      phone: '',
      serviceType: '',
      message: '',
    },
  })

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true)
    await new Promise(resolve => setTimeout(resolve, 1500))
    console.log('Form submitted:', data)
    toast({
      title: t.contactPage.form.successTitle,
      description: t.contactPage.form.successDescription,
    })
    form.reset()
    setIsSubmitting(false)
  }

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: t.contactPage.info.office,
      content: 'Unit 4206, Mazaya Business Avenue BB2 - JLT, Dubai, UAE',
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: t.contactPage.info.phone,
      content: '—',
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: t.contactPage.info.email,
      content: 'contact@shonchin.com',
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: t.contactPage.info.hours,
      content: t.contactPage.info.hoursContent,
    },
  ]

  const socialLinks = [
    { icon: <SiLinkedin className="w-5 h-5" />, label: 'LinkedIn', href: '#' },
    { icon: <SiX className="w-5 h-5" />, label: 'X (Twitter)', href: '#' },
    { icon: <SiWechat className="w-5 h-5" />, label: t.contactPage.info.followUs, href: '#' },
  ]

  return (
    <div className="min-h-screen">
      <section className="relative h-[50vh] bg-charcoal text-charcoal-foreground overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal/95 to-charcoal" />
        <div className="container mx-auto px-4 h-full flex items-center justify-center relative z-10">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            className="text-center max-w-3xl"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {t.contactPage.hero.title}
            </h1>
            <p className="text-xl text-charcoal-foreground/80">
              {t.contactPage.hero.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-cream">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div variants={staggerItem}>
                <h2 className="text-3xl font-bold mb-4">
                  {t.contactPage.form.title}
                </h2>
                <p className="text-muted-foreground mb-8">
                  {t.contactPage.form.description}
                </p>
              </motion.div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t.contactPage.form.nameLabel} *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t.contactPage.form.namePlaceholder}
                              className="bg-background border-border rounded-xl"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t.contactPage.form.companyLabel} *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t.contactPage.form.companyPlaceholder}
                              className="bg-background border-border rounded-xl"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t.contactPage.form.emailLabel} *</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder={t.contactPage.form.emailPlaceholder}
                              className="bg-background border-border rounded-xl"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t.contactPage.form.phoneLabel} *</FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder={t.contactPage.form.phonePlaceholder}
                              className="bg-background border-border rounded-xl"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="serviceType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t.contactPage.form.serviceTypeLabel} *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-background border-border rounded-xl">
                              <SelectValue placeholder={t.contactPage.form.serviceTypePlaceholder} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="digital-twin">{t.contactPage.form.serviceOptions.digitalTwin}</SelectItem>
                            <SelectItem value="vertical-strategy">{t.contactPage.form.serviceOptions.verticalStrategy}</SelectItem>
                            <SelectItem value="cross-domain">{t.contactPage.form.serviceOptions.crossDomain}</SelectItem>
                            <SelectItem value="other">{t.contactPage.form.serviceOptions.other}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t.contactPage.form.messageLabel} *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t.contactPage.form.messagePlaceholder}
                            className="bg-background border-border rounded-xl min-h-[150px] resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary text-primary-foreground rounded-xl px-6 py-3 font-semibold hover:opacity-90 transition-all"
                  >
                    {isSubmitting ? (
                      t.contactPage.form.submitting
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        {t.contactPage.form.submitButton}
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="space-y-8"
            >
              <motion.div variants={staggerItem}>
                <h2 className="text-3xl font-bold mb-6">
                  {t.contactPage.info.title}
                </h2>
                <div className="space-y-4">
                  {contactInfo.map((info, index) => (
                    <motion.div
                      key={index}
                      variants={staggerItem}
                      className="flex gap-4 p-6 bg-background rounded-2xl border border-border shadow-sm"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                        {info.icon}
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">
                          {info.title}
                        </h3>
                        <p className="text-muted-foreground whitespace-pre-line text-sm">
                          {info.content}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={staggerItem}>
                <h3 className="text-xl font-semibold mb-4">
                  {t.contactPage.info.followUs}
                </h3>
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-12 h-12 rounded-xl bg-background border border-border hover:border-primary hover:bg-primary/5 flex items-center justify-center text-muted-foreground hover:text-primary transition-all"
                      aria-label={social.label}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm">
              <div className="p-8">
                <h3 className="text-2xl font-bold mb-4">
                  {t.contactPage.info.mapTitle}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {t.contactPage.info.mapDescription}
                </p>
              </div>
              <div className="aspect-video bg-muted">
                <img
                  src={IMAGES.DUBAI_OFFICE_3}
                  alt="Dubai Office"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-charcoal text-charcoal-foreground">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">
              {t.contactPage.cta.title}
            </h2>
            <p className="text-charcoal-foreground/80 mb-8 text-lg">
              {t.contactPage.cta.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-primary text-primary-foreground rounded-xl px-8 py-3 font-semibold hover:opacity-90 transition-all"
              >
                {t.contactPage.cta.bookButton}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-charcoal-foreground/30 text-charcoal-foreground hover:bg-charcoal-foreground/10 rounded-xl px-8 py-3 font-semibold"
              >
                {t.contactPage.cta.downloadButton}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}